import { Lightning } from '@lightningjs/sdk';
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import { safetyMargins, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { t } from '../../i18n';
import HeroOverlay from '../hero/heroOverlay/heroOverlay.component';
import Progress from '../progress/progress.component';
import Icon from '../icon/icon.component';
import { contain, cover } from '../utils/textures';
import { HIDE_BACKGROUND_SIGNAL, SELECTED_SIGNAL, SHOW_BACKGROUND_SIGNAL } from '../signals';
import {
  templates,
  sizes,
  imageOptions,
  SHOW_SERVICE_ICON,
  Z_INDEX_NORMAL,
  Z_INDEX_WITH_PROGRAM_INFO,
  HIGHLIGHT_SEPARATOR,
  SHOW_DETAILS,
  SHOW_HIGHLIGHT,
  SHOW_EXTRA_HIGHLIGHT,
  SHOW_CONTENT_ADVISORY,
  SHOW_BOTTOM_ROW,
  BUTTON_TYPES,
} from './jumbotron.constants';
import {
  LOGO_DIMENSIONS,
  defaultContext,
  FLEX_CONTAINER_WIDTH,
  colors,
  getInitialStyle,
  getServiceIconViewBox,
  getBasicIconViewBox,
  getContentAdvisoryIconStyle,
  LAYOUT_BOTTOM_MARGIN,
  TRAILER_ICON,
  BACKGROUND_CONTENT_OFFSET,
} from './jumbotron.style';
import { isVisibleInformationButton } from './jumbotron.utils';
import { makeTextPatch } from '../utils/templates';
import { makeProgressBarProperties } from '../progress/progress.utils';
import { isVisibleIcon, isVisibleCTA } from '../solo/solo.utils';
import { PodcastInformation } from '../podcast-information/podcastInformation.component';
import { PROGRAM_INFORMATION_CLOSED_SIGNAL } from '../programInformation/programInformation.constants';
import Incentive from '../incentive/incentive.component';
import { getLinesCount } from '../incentive/incentive.utils';
import { sendEvent } from '../../../logic/analytics/analytics.utils';
import {
  getProgramInformationFocusedStyle,
  getProgramInformationUnFocusedStyle,
} from '../podcast-information/podcastInformation.style';
import { TrailerPlayer } from './trailer/trailer.component';
import { VIDEO_CAN_PLAY_SIGNAL, VIDEO_ENDED_SIGNAL, VIDEO_PLAYING_SIGNAL } from './trailer/trailer.constants';

/**
 * A large format that allows a single program to be highlighted on a full page. It is often found at the top of a program page.
 *
 * @version 2.11.0
 * @see https://zeroheight.com/726d5c282/p/7741ec-jumbotron
 */
export default class Jumbotron extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {},
        Gradient: {
          type: HeroOverlay,
        },
        Trailer: {
          type: TrailerPlayer,
          signals: {
            [VIDEO_CAN_PLAY_SIGNAL]: '_handleVideoCanPlay',
            [VIDEO_PLAYING_SIGNAL]: '_handleVideoPlaying',
            [VIDEO_ENDED_SIGNAL]: '_handleVideoEnded',
          },
        },
        FlexContainer: {
          Logo: {},
          Incentive: {
            type: Incentive,
          },
          ServiceIcon: {
            type: Icon,
          },
          MetadataRow: {
            Highlight: {},
            ExtraTitle: {},
            ExtraHighlightRow: {
              ExtraHighlight: {},
              ContentAdvisory: {
                IconsRow: {},
                ContentRatingIcon: {
                  type: Icon,
                },
              },
            },
            Description: {},
            Details: {},
          },
          ProgressBar: {
            type: Progress,
          },
          ButtonsRow: {
            PlayButton: {
              type: BUTTON_TYPES.PLAY_BUTTON,
            },
            MyListButton: {
              type: BUTTON_TYPES.MY_LIST_BUTTON,
              passSignals: {
                [SELECTED_SIGNAL]: true,
              },
            },
            InfoButton: {
              type: BUTTON_TYPES.INFO_BUTTON,
            },
            TrailerButton: {
              type: BUTTON_TYPES.TRAILER_BUTTON,
            },
          },
          BottomRow: {
            ExtraDetails: {},
            ContentRatingIcon: {
              type: Icon,
            },
          },
        },
        ProgramInformation: {
          type: PodcastInformation,
          signals: {
            [PROGRAM_INFORMATION_CLOSED_SIGNAL]: '_handleProgramInformationClose',
          },
        },
      },
      getInitialStyle(),
    );
  }

  static getBottomMargin({ screenSize }) {
    return LAYOUT_BOTTOM_MARGIN[screenSize];
  }

  _construct() {
    this._buttons = [];
    this._index = 0;
    this._style = defaultContext;
    this._showingTrailer = false;
  }

  _attach() {
    this._trailerStarted = false;
    this._trailerComplete = false;
  }

  _detach() {
    this.tag('Background').removeAllListeners('txLoaded');
    if (this._hideBackgroundTimeout) {
      clearTimeout(this._hideBackgroundTimeout);
    }
    this._showingTrailer = false;
    this._focused = null;
    this._trailerStarted = false;
    this._trailerComplete = false;
  }

  get item() {
    return this._item;
  }

  set item({ content, blockIndex, itemIndex, config, colorScheme = defaultContext.colorMode }) {
    const currentImage = _get(this._item, 'image.src');

    this._item = content;

    const { screenSize } = this._style;
    const textColor = colors.text[colorScheme];
    const {
      image,
      logo,
      description,
      details,
      highlight,
      extraTitle,
      extraHighlight,
      extraDetails,
      progress,
      action,
      servicePicto,
      csaPicto,
      bookmark,
      secondaryActions,
      incentives,
    } = this._item;

    const contentAdvisoryPictos = _get(this._item, 'contentAdvisory.pictos', []);
    const isContentAdvisoryVisible = SHOW_CONTENT_ADVISORY && contentAdvisoryPictos.some(value => Boolean(value));
    const isContentRatingIconVisible = !SHOW_CONTENT_ADVISORY && !SHOW_BOTTOM_ROW;
    const isExtraHighlightRowVisible =
      (SHOW_EXTRA_HIGHLIGHT && extraHighlight) || isContentAdvisoryVisible || isContentRatingIconVisible;
    const logoSrc = (logo && logo.src) || '';
    const isLogoVisible = Boolean(logoSrc);
    const serviceIconVisible = isVisibleIcon(servicePicto);
    const incentiveLines = getLinesCount(incentives);
    const highlightText = [highlight, extraDetails].filter(Boolean).join(HIGHLIGHT_SEPARATOR);
    const textWidth = FLEX_CONTAINER_WIDTH[screenSize];

    this._trailer = secondaryActions[1];
    const trailerPicto = _get(this._trailer, 'picto', TRAILER_ICON);
    const trailerLabel = _get(this._trailer, 'label', t('trailer.actionLabel'));
    const trailerId = _get(this._trailer, 'target.value_layout.id');

    const patch = {
      Trailer: {
        asset: trailerId,
      },
      FlexContainer: {
        Logo: {
          visible: isLogoVisible,
          texture: {
            ...contain(logoSrc, LOGO_DIMENSIONS[screenSize].width, LOGO_DIMENSIONS[screenSize].height),
            hasAlpha: true,
          },
        },
        Incentive: {
          visible: incentiveLines > 0,
          data: {
            linesCount: incentiveLines,
            w: FLEX_CONTAINER_WIDTH[screenSize],
            incentives,
            colorMode: this._style.colorMode,
          },
        },
        ServiceIcon: {
          visible: SHOW_SERVICE_ICON && serviceIconVisible,
          iconData: {
            picto: servicePicto,
            viewBox: getServiceIconViewBox(screenSize),
          },
        },
        MetadataRow: {
          Highlight: makeTextPatch(SHOW_HIGHLIGHT ? highlightText : '', textWidth, textColor),
          ExtraTitle: makeTextPatch(extraTitle, textWidth, textColor),
          ExtraHighlightRow: {
            visible: isExtraHighlightRowVisible,
            ExtraHighlight: makeTextPatch(SHOW_EXTRA_HIGHLIGHT ? extraHighlight : '', textWidth, textColor),
            ContentAdvisory: {
              IconsRow: {
                visible: isContentAdvisoryVisible,
              },
              ContentRatingIcon: {
                visible: isContentRatingIconVisible,
                iconData: {
                  picto: csaPicto,
                  viewBox: getBasicIconViewBox(screenSize),
                },
              },
            },
          },
          Description: makeTextPatch(description, textWidth, textColor),
          Details: makeTextPatch(SHOW_DETAILS ? details : '', textWidth, textColor),
        },
        ProgressBar: makeProgressBarProperties(progress),
        ButtonsRow: {
          PlayButton: {
            visible: isVisibleCTA(action),
            label: _get(action, 'label', ''),
            picto: _get(action, 'picto', ''),
          },
          MyListButton: {
            visible: Boolean(bookmark),
            label: t('bookmarks.actionLabel'),
            item: bookmark,
            blockIndex,
            itemIndex,
          },
          InfoButton: {
            visible: isVisibleInformationButton(this._item),
            label: t('contentInfo.actionLabel'),
          },
          TrailerButton: {
            visible: this.tag('Trailer').enabled
              ? Boolean(this._trailer && this._trailerComplete)
              : Boolean(this._trailer),
            picto: trailerPicto,
            label: trailerLabel,
          },
        },
        BottomRow: {
          visible: SHOW_BOTTOM_ROW,
          ExtraDetails: makeTextPatch(extraDetails, FLEX_CONTAINER_WIDTH[screenSize], textColor),
          ContentRatingIcon: {
            iconData: {
              picto: csaPicto,
              viewBox: getBasicIconViewBox(screenSize),
            },
          },
        },
      },
      ProgramInformation: {
        data: this._item,
      },
    };

    if (currentImage !== image.src) {
      this.tag('Background').once('txLoaded', () => {
        this.tag('Background').setSmooth('alpha', 1);
      });

      patch.Background = {
        texture: cover(image.src, config.w, config.h),
        alpha: this.stage.textureManager.textureSourceHashmap.has(image.src) ? 1 : 0.0001,
      };
    }
    this.patch(patch);

    if (isContentAdvisoryVisible) {
      this.tag('IconsRow').children = contentAdvisoryPictos.map(picto => ({
        type: Icon,
        ...getContentAdvisoryIconStyle(picto, this._style.screenSize),
      }));
    }
    const items = this.tag('ButtonsRow').children;
    this._buttons = items.filter(item => item.visible);
    this._setIndex();
  }

  set contentPadding(value) {
    const { screenSize } = this._style;
    const x = Number.isFinite(value) ? value : 0;
    const backgroundOffset = BACKGROUND_CONTENT_OFFSET[screenSize];
    const backgroundSize = imageOptions[templates.Jumbotron][RENDERING_CONTEXT.GENERAL][screenSize].PRIMARY;
    const w = backgroundSize.width - x + backgroundOffset;
    const h = Math.round(backgroundSize.height * (w / backgroundSize.width));

    this.patch({
      Background: { w, h },
      Gradient: { w, h },
      Trailer: { w, h },
      FlexContainer: {
        x,
        ProgressBar: {
          w: w - safetyMargins.horizontal[screenSize] * 2,
        },
      },
    });
  }

  _getFocused() {
    return this._focused;
  }

  _focus() {
    this._isFocused = true;
    this._playTrailer();
    // In case the trailer is already started due to back button navigation to the programm page while Jumbotron was not in focus
    if (this._trailerStarted) {
      this._showTrailer();
    }
  }

  _unfocus() {
    this._isFocused = false;
    this.tag('Trailer').pause();
    this._hideTrailer();
  }

  _playTrailer() {
    // NOTE: auto-play is disabled after a trailer is complete and should be re-enabled via a corresponding button
    if (this._trailerComplete) {
      return;
    }

    this._hideBackgroundTimeout = setTimeout(() => {
      this.tag('Trailer').play();
    }, 150);
  }

  _showTrailer() {
    if (!this._isFocused) {
      return;
    }
    this._showingTrailer = true;
    this.tag('Background').setSmooth('alpha', 0.0001);
    this.signal(HIDE_BACKGROUND_SIGNAL);
  }

  _hideTrailer() {
    this._showingTrailer = false;
    this.tag('Background').setSmooth('alpha', 1);
    this.signal(SHOW_BACKGROUND_SIGNAL);
  }

  _handleRight() {
    if (this._index < this._buttons.length - 1) {
      this._setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _handleLeft() {
    if (this._index > 0) {
      this._setIndex(this._index - 1);

      return true;
    }

    return false;
  }

  _setIndex(index = this._index) {
    this._index = index;
    this._focused = this._buttons[this._index];
  }

  _handleEnter() {
    if (this._focused === this.tag('InfoButton')) {
      this._showProgramInformation();
      sendEvent({ key: 'pressMoreInfo' });
    } else if (this._focused === this.tag('TrailerButton')) {
      this.signal(SELECTED_SIGNAL, {
        id: 'trailer',
        action: this._trailer,
      });
    } else {
      this.signal(SELECTED_SIGNAL, this._item);
    }
  }

  _showProgramInformation() {
    this.zIndex = Z_INDEX_WITH_PROGRAM_INFO;
    this.tag('ProgramInformation').patch(getProgramInformationFocusedStyle());
    this._focused = this.tag('ProgramInformation');
  }

  _handleProgramInformationClose() {
    this.zIndex = Z_INDEX_NORMAL;
    this.tag('ProgramInformation').patch(getProgramInformationUnFocusedStyle());
    this._setIndex();
    sendEvent({ key: 'closeMoreInfo' });
  }

  _handleVideoCanPlay() {
    if (this._isFocused) {
      this._playTrailer();
    }
  }

  _handleVideoPlaying() {
    if (!this._trailerStarted) {
      sendEvent({ key: 'trailerLaunch' });
    }
    this._trailerStarted = true;
    this._trailerComplete = false;
    // In case of a back button navigation to the programm page while Jumbotron was not in focus
    if (!this._isFocused) {
      this.tag('Trailer').pause();

      return;
    }
    this._showTrailer();
  }

  _handleVideoEnded() {
    if (!this._trailerComplete) {
      sendEvent({ key: 'trailerCompletion100' });
    }
    this._trailerComplete = true;
    this._trailerStarted = false;
    this._hideTrailer();
    this.tag('TrailerButton').visible = true;
    this._buttons = this.tag('ButtonsRow').children.filter(item => item.visible);
  }
}

Jumbotron.Templates = templates;
Jumbotron.Sizes = sizes;
Jumbotron.ImageOptions = imageOptions;
